/**
 * O painel do dado (E203.F2, ADR-0146 §1–§4): o clique numa tabela, numa linha ou num texto
 * protegido do documento abre, ao lado do bloco, um painel não modal, que se arrasta pela barra do
 * alto e deixa o documento à vista. Mostra o que está impresso e, abaixo, o conteúdo da origem
 * (os campos que se ajustam e onde gravam, ou, para a prova e o método, por que não mudam).
 *
 * Um painel aberto por vez. Esc fecha e devolve o foco ao bloco clicado. O que se digita no painel
 * o deixa "sujo": fechar pergunta antes, e a mesa pergunta antes de sair (`SairSemPerder`).
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

import type { PedidoDoDado } from './pedidoDoDado';
import { alturaMaxima, LARGURA_DO_PAINEL, usePosicaoDoPainel } from './posicaoDoPainel';

const t = ptBR.painelDoDado;

export interface AcoesDoPainel {
  /** Fecha o painel (com a pergunta, se houver alteração não salva). */
  fechar: () => void;
  /** O conteúdo gravou: o painel deixa de estar sujo. */
  salvo: () => void;
}

export interface PainelDoDadoProps {
  pedido: PedidoDoDado;
  onFechar: () => void;
  /** Avisa a mesa quando há alteração não salva (`SairSemPerder`). */
  onSujo?: (sujo: boolean) => void;
  /** Muda quando outro bloco foi clicado com este painel sujo: o painel pergunta antes. */
  insistir?: number;
  children: (acoes: AcoesDoPainel) => ReactNode;
}

function Descartar({ onFicar, onSair }: { onFicar: () => void; onSair: () => void }) {
  return (
    <div
      role="alertdialog"
      aria-label={t.descartar.titulo}
      className="border-warning-200 space-y-sc-2 border-b bg-warning-50 p-sc-3 text-body-sm"
      data-testid="painel-do-dado-descartar"
    >
      <p className="font-semibold text-fg">{t.descartar.titulo}</p>
      <p className="text-fg-muted">{t.descartar.texto}</p>
      <div className="flex gap-sc-2">
        <Button type="button" size="sm" variant="ghost" onClick={onFicar}>
          {t.descartar.ficar}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onSair}>
          {t.descartar.sair}
        </Button>
      </div>
    </div>
  );
}

export function PainelDoDado({
  pedido,
  onFechar,
  onSujo,
  insistir = 0,
  children,
}: PainelDoDadoProps) {
  const idDoTitulo = useId();
  const titulo = useRef<HTMLHeadingElement>(null);
  const [sujo, setSujo] = useState(false);
  const [perguntando, setPerguntando] = useState(false);
  const { posicao, alca } = usePosicaoDoPainel(pedido.elemento);

  const marcar = useCallback(
    (valor: boolean) => {
      setSujo(valor);
      onSujo?.(valor);
    },
    [onSujo],
  );
  const sair = useCallback(() => {
    marcar(false);
    onFechar();
    pedido.elemento?.focus();
  }, [marcar, onFechar, pedido.elemento]);
  const fechar = useCallback(() => (sujo ? setPerguntando(true) : sair()), [sujo, sair]);

  useEffect(() => titulo.current?.focus(), []);
  useEffect(() => {
    if (insistir > 0) setPerguntando(true);
  }, [insistir]);
  useEffect(() => {
    const tecla = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        fechar();
      }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [fechar]);

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={idDoTitulo}
      className="fixed z-40 flex flex-col border border-border bg-bg shadow-lg"
      style={{
        left: posicao.x,
        top: posicao.y,
        width: LARGURA_DO_PAINEL,
        maxHeight: alturaMaxima(posicao),
      }}
      onInputCapture={() => !sujo && marcar(true)}
      data-testid="painel-do-dado"
      data-origem={`${pedido.origem.tipo}:${pedido.origem.chave}`}
    >
      <header
        className="flex cursor-move touch-none select-none items-center justify-between gap-sc-2 border-b border-border bg-surface-2 px-sc-3 py-sc-2"
        title={t.arrastar}
        data-testid="painel-do-dado-alca"
        {...alca}
      >
        <h2
          id={idDoTitulo}
          ref={titulo}
          tabIndex={-1}
          className="text-body-md font-semibold text-fg outline-none"
        >
          {t.titulo[pedido.origem.tipo]}
        </h2>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={fechar}
          aria-label={t.fechar}
          data-testid="painel-do-dado-fechar"
        >
          ×
        </Button>
      </header>
      {perguntando ? <Descartar onFicar={() => setPerguntando(false)} onSair={sair} /> : null}
      <div className="space-y-sc-3 overflow-y-auto p-sc-3">
        {pedido.impresso !== '' ? (
          <div className="text-body-sm" data-testid="painel-do-dado-impresso">
            <p className="text-caption font-semibold text-fg-dim">{t.impresso}</p>
            <p className="text-fg">{pedido.impresso}</p>
          </div>
        ) : null}
        {children({ fechar, salvo: () => marcar(false) })}
      </div>
    </section>
  );
}
